import cx from 'classnames'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Styles from './YouTubeShortPreview.module.scss'
import { YouTubeIntegration } from 'api/clients/ExternalIntegrationsClient'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { LikeIcon, CommentIcon, ShareIcon, DislikeIcon } from './icons'
import { ArrowRefresh2, Search, MoreDots } from 'blaze-ui/icons/20'
import { highlightContent } from '../highlightContent'
import { MediaObject } from 'types'
import { isVideo } from 'components/ImagePickerModal/utils'
import { Button } from 'blaze-ui/Button'
import { useVideoProgress } from '../hooks/useVideoProgress'
import { ReactNode } from 'react'
import { ReelsVideoPlayer } from '../components/ReelsVideoPlayer/ReelsVideoPlayer'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditInDesignerOverlay } from '../components/EditInDesignerOverlay/EditInDesignerOverlay'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'
import { ScheduledPostShow, YoutubePostSettings } from 'api/clients/ScheduledPostsClient'

interface YouTubeShortPreviewProps {
  integration?: YouTubeIntegration
  mediaObjects?: MediaObject[]
  containerClassName?: string
  customPreviewContent?: ReactNode
  showVideoControls?: boolean
  isActive?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  post?: ScheduledPostShow
  isGenerating?: boolean
  onClose?: () => void
}

export const YouTubeShortPreview = ({
  integration,
  post,
  mediaObjects,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  isActive = true,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
}: YouTubeShortPreviewProps) => {
  const { name, username, avatar } = integration
    ? extractProfileInfo(integration)
    : { name: 'User', username: 'user', avatar: Silhouette }

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const target = post?.scheduled_post_targets.find((target) => target.provider === 'youtube')
  const postSettings = target?.target_overrides?.post_settings as YoutubePostSettings
  const title = postSettings?.title || post?.source_title || ''

  const video = mediaObjects?.length && isVideo({ url: mediaObjects[0].source_url, type: mediaObjects[0].mime_type })
  const videoUrl = video ? mediaObjects[0].source_url : undefined
  const videoDuration = mediaObjects?.[0]?.duration

  const { videoRef, progress } = useVideoProgress({ videoUrl, duration: videoDuration })

  return (
    <ConditionalWrapper
      condition={showHoverActionOverlay && almanacFileKey}
      wrapper={(children) => (
        <EditInDesignerOverlay
          almanacFileKey={almanacFileKey as string}
          borderRadius={16}
          position={video ? 'top' : undefined}
          onClose={onClose}
          className={isGenerating ? Styles.disableHover : undefined}
        >
          {children}
        </EditInDesignerOverlay>
      )}
    >
      <div className={cx(Styles.container, containerClassName)}>
        <div className={Styles.content}>
          {customPreviewContent || (
            <ReelsVideoPlayer
              mediaObjects={mediaObjects}
              showVideoControls={showVideoControls}
              isActive={isActive}
              className={Styles.preview}
              dataTestId="youtube-short-video"
              username={username}
              videoRef={videoRef}
              almanacFileKey={almanacFileKey as string}
              isGenerating={isGenerating}
            />
          )}
        </div>
        <div className={Styles.footer}>
          <div className={Styles.videoInfo}>
            <div className={Styles.profileInfo}>
              <img src={avatar || Silhouette} alt={username} className={Styles.avatar} />
              <div className={Styles.username}>{`@${name}`}</div>
              <Button variant="tertiary" className={Styles.subscribeButton} size="xs" data-testid="subscribe-button">
                Subscribe
              </Button>
            </div>
            <div className={Styles.caption}>
              {isGenerating ? (
                <CaptionSkeleton />
              ) : (
                <span
                  key={`caption-${title}`}
                  dangerouslySetInnerHTML={{
                    __html: highlightContent(title, highlightClassNames),
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className={Styles.topActions}>
          <Search />
          <div className={Styles.moreDots}>
            <MoreDots />
          </div>
        </div>
        <div className={Styles.actions}>
          <div className={Styles.action}>
            <LikeIcon />
            <span>223</span>
          </div>
          <div className={Styles.action}>
            <DislikeIcon />
            <span>23</span>
          </div>
          <div className={Styles.action}>
            <CommentIcon />
            <span>10</span>
          </div>
          <div className={Styles.action}>
            <ShareIcon />
            <span>3</span>
          </div>
          <div className={Styles.action}>
            <ArrowRefresh2 />
            <span>Remix</span>
          </div>
          <img src={avatar || Silhouette} alt={username} className={Styles.avatarFooter} data-testid="avatar-footer" />
        </div>
        <div className={Styles.loadingBar}>
          <div className={Styles.progress} style={{ width: `${progress}%` }} />
        </div>
      </div>
    </ConditionalWrapper>
  )
}
